/**
 * Composant Budget - Budget annuel d'investissement
 * Affichage statique du budget en tableau structuré
 */

import { type DocumentComponent, type DocumentProps } from "../../lib/types";
import { cn } from "@repo/utils";
import { createElement } from "react";
import { Card, CardContent, Separator } from "@repo/ui";

type BudgetLine = {
  label: string;
  amount: number;
  isTotal?: boolean;
};

function formatAmount(amount: number): string {
  return amount.toLocaleString("fr-FR") + " €";
}

function BudgetTable({
  title,
  lines,
}: {
  title: string;
  lines: BudgetLine[];
}) {
  return (
    <div>
      <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground">
        {title}
      </h4>
      <table className="w-full">
        <tbody>
          {lines.map((line, i) => (
            <tr
              key={i}
              className={cn(
                line.isTotal
                  ? "border-t-2 border-foreground/20 font-semibold"
                  : i % 2 === 0
                    ? "bg-muted/40"
                    : "",
              )}
            >
              <td
                className={cn(
                  "rounded-l-md py-2.5 pl-3 text-sm",
                  line.isTotal ? "pt-3" : "",
                )}
              >
                {line.label}
              </td>
              <td
                className={cn(
                  "rounded-r-md py-2.5 pr-3 text-right text-sm tabular-nums",
                  line.isTotal ? "pt-3" : "",
                )}
              >
                {formatAmount(line.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BudgetDisplay() {
  const currentYear = new Date().getFullYear();

  const investissements: BudgetLine[] = [
    { label: "Bâtiment - Agencement", amount: 44496 },
    { label: "Machine de production", amount: 16800 },
    { label: "Chaudière", amount: 43200 },
    { label: "Total Investissements", amount: 104496, isTotal: true },
  ];

  const financements: BudgetLine[] = [
    { label: "Autofinancement", amount: 37496 },
    { label: "Crédit Moyen terme (Crédit National)", amount: 44000 },
    { label: "Subvention de l'état", amount: 23000 },
    { label: "Total Financements", amount: 104496, isTotal: true },
  ];

  return (
    <div className="mx-auto max-w-lg">
      <Card className="gap-0">
        <CardContent className="space-y-6 pt-6">
          {/* Header */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/logos/sytel_grand_est.png"
              alt="Logo entreprise"
              className="h-20 w-20 object-contain"
            />
            <div className="text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Service du Budget
              </p>
              <h3 className="text-base font-semibold text-foreground">
                Budget annuel d'investissement {currentYear}
              </h3>
            </div>
          </div>

          <Separator />

          <BudgetTable title="Investissements" lines={investissements} />

          <Separator />

          <BudgetTable title="Financements" lines={financements} />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Composant Budget avec métadonnées
 */
export const Budget: DocumentComponent = function Budget(
  _props: DocumentProps,
) {
  return createElement(BudgetDisplay);
};

Budget.metadata = {
  id: "budget",
  titre: "Budget annuel",
  icon: "budget",
};

export default Budget;
